import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController } from '@ionic/angular';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AppRoutes } from 'src/app/app.routes';
import { ToastPosition, ToastService } from 'src/app/core/services/toast.service';
import { AccountService } from 'src/app/core/services/account.service';
import { AuthAction } from 'src/app/core/authentication/auth-action';
import { AuthService } from 'src/app/core/authentication/auth.service';
import { environment } from 'src/environments/environment';
import { CallRoute } from './call.routes';
import { OutgoinCall } from './models/outgoin-call';
import { CallOptions, CallState, LoginOptions, RegistrationState, SipCall, SipCallEvent } from 'sip-call';
import { FirebaseCrashlytics } from '@capacitor-community/firebase-crashlytics';

@Injectable({
    providedIn: 'root'
})
export class CallService {
    speakerEnabled: boolean = false;
    microphoneMuted: boolean = false;
    onCallStateChanged: Subject<CallState> = new Subject<CallState>();
    currentOutgoinCall?: OutgoinCall;

    constructor(
        private authService: AuthService,
        private accountService: AccountService,
        private toastService: ToastService,
        private router: Router,
        private alertController: AlertController,
    ) {
    }

    async initSipCall(): Promise<void> {
        this.accountService.getBranch()
            .subscribe(async (branch) => {
                this.setupEventHandlers();
                const permissionStatus = await SipCall.checkPermissions();
                if (permissionStatus.calling !== 'granted') {
                    await SipCall.requestPermissions();
                }
                const loginOptions: LoginOptions = {
                    username: environment.sipCall.branches[branch.id].username,
                    password: environment.sipCall.branches[branch.id].password,
                    realm: environment.sipCall.realm
                }
                await SipCall.login(loginOptions);
            });

        this.authService.onAuthChanged
            .pipe(
                filter((authAction) => authAction == AuthAction.Logout),
            ).subscribe(async () => {
                await SipCall.logout();
                SipCall.removeAllListeners();
            });
    }

    call(outgoinCall: OutgoinCall): void {
        this.showCallConfirmAlert()
            .then(() => {
                this.currentOutgoinCall = outgoinCall;
                this.router.navigate([AppRoutes.Call, CallRoute.Outgoing])
                    .then(async () => {
                        const callOptions: CallOptions = {
                            address: outgoinCall.callee.number,
                            displayName: outgoinCall.caller.name
                        };
                        await SipCall.call(callOptions);
                    })
            }).catch(() => { });
    }

    toggleSpeaker(): void {
        SipCall.setSpeakerEnabled({ enabled: !this.speakerEnabled });
        this.speakerEnabled = !this.speakerEnabled;
    }

    toggleMute(): void {
        SipCall.setMicrophoneMuted({ muted: !this.microphoneMuted });
        this.microphoneMuted = !this.microphoneMuted;
    }

    hangup(): void {
        SipCall.hangup();
    }

    private async showCallConfirmAlert(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            this.alertController
                .create({
                    header: 'Ben je zeker dat je deze klant wil bellen?',
                    buttons: [
                        {
                            text: 'Annuleren',
                            role: 'cancel',
                            handler: () => reject(false),
                        },
                        {
                            text: 'Bevestigen',
                            handler: () => resolve(true),
                        }
                    ]
                })
                .then(alert => {
                    alert.present();
                });
        });
    }

    private setupEventHandlers(): void {
        SipCall.addListener(SipCallEvent.AccountRegistrationEvent, ({ value }: any) => {
            console.log('AccountRegistrationEvent')
            console.log(value)
            if (value == RegistrationState.Failed) {
                FirebaseCrashlytics.recordException({ message: 'SipCallEvent.AccountRegistrationEvent: RegistrationState.Failed' });
                this.toastService.showErrorToast('Bellen is momenteel niet mogelijk', ToastPosition.Top);
            }
        });

        SipCall.addListener(SipCallEvent.CallStateEvent, ({ value }: any) => {
            console.log('CallStateEvent')
            console.log(value)
            if (value == CallState.Error) {
                FirebaseCrashlytics.recordException({ message: 'SipCallEvent.CallStateEvent: CallState.Error' });
                this.toastService.showErrorToast('Probleem met bellen probeer later opnieuw', ToastPosition.Top);
            } else if (value == CallState.Ended) {
                this.currentOutgoinCall = undefined;
                this.router.navigate([AppRoutes.Courier]);
            }
        });
    }
}